import { Trans, useTranslation } from 'next-i18next'

import { useRouter } from 'next/router'

import { InitialPage } from '~/auth/components/InitialPage'
import { useSignupScenario } from '~/auth/hooks/useSignupScenario'
import { Breakpoint, useCheckBreakpoint } from '~/utils'
import ResponsiveImage from '~/components/Image/ResponsiveImage'
import { Footer } from '~/components'
import { TermsAndConditions } from '~/auth/components/TermsAndConditions'
import SellPointBox from '~/auth/components/SellPointBox'
import { AppLanguage } from '~/types/locale'
import { useGetUserLocationInfoFromIpQuery } from '~/redux/apis/geo-location-api'

export default function Register(): React.ReactElement {
  const { hidePasswordFields, showRegisterForm } = useSignupScenario()
  const { t } = useTranslation('start')
  const { data } = useGetUserLocationInfoFromIpQuery()
  const isNorwegian = data?.countryCode === AppLanguage.NO.toUpperCase()
  const router = useRouter()
  const isNoLang = router.locale === AppLanguage.NO
  const isUSA = data?.countryCode === 'US'
  const CarriculumText = isNorwegian
    ? t('registerAccount:norwayCurriculum')
    : isUSA
    ? t('registerAccount:californiaStandards')
    : t('registerAccount:curriculumAligned')
  // Determine background image based on screen size

  const isSmallScreen = useCheckBreakpoint({ lt: Breakpoint.LG })
  const bgSrcKey = isSmallScreen
    ? isNoLang
      ? 'landing/start/bgm_no_v2.jpg'
      : 'landing/start/bgm_en_v2.jpg'
    : isNoLang
    ? 'landing/start/bg-image_no_v2.jpg'
    : 'landing/start/bg-image_en_v2.jpg'
  const bgHeight = isSmallScreen ? 548 : 800
  const bgWidth = isSmallScreen ? 360 : 1936
  const bgMediaQuery = [
    {
      screen: '(max-width: 1023px)',
      width: '360px',
      srcKey: isNoLang ? 'landing/start/bgm_no_v2.jpg' : 'landing/start/bgm_en_v2.jpg',
    },
    {
      screen: '(min-width: 1024px)',
      width: '1280px',
      srcKey: isNoLang ? 'landing/start/bg-image_no_v2.jpg' : 'landing/start/bg-image_en_v2.jpg',
    },
  ]

  return (
    <>
      <div className="relative bg-gradient-sign-up lg:min-h-screen">
        <ResponsiveImage
          alt={t('intro.heading')}
          fetchPriority="high"
          height={bgHeight}
          imageClassName="fixed lg:h-screen w-full lg:object-cover"
          imageWidths="[360, 1936]"
          loading="eager"
          mediaQuery={bgMediaQuery}
          srcKey={bgSrcKey}
          width={bgWidth}
        />
        <div className="flex flex-col lg:py-5 items-center justify-center w-full min-h-screen overflow-x-hidden bg-black/70 backdrop-blur-0">
          <div className="bg-white rounded text-black px-4 py-6 md:px-15 md:py-10 m-4 max-w-screen-19/20">
            <InitialPage hidePasswordFields={hidePasswordFields} showRegisterForm={showRegisterForm} />
          </div>
          <div className="flex flex-col justify-center items-center text-white my-4">
            <h3 className="text-menu text-center font-bold">{t('registerAccount:trustedRegisteredUsers')}</h3>
            <TermsAndConditions className="w-69" />
          </div>
          <div className="flex flex-col lg:flex-row gap-3 lg:gap-6 mb-4">
            <SellPointBox
              imageClassName="lg:-ml-8"
              imageSize={{ width: 100, height: 66 }}
              imageSrcKey="curriculum/Brain.png"
              text={Trans({
                t,
                i18nKey: 'registerAccount:aiMathSuperpower',
                components: { br: <br /> },
                children: t('registerAccount:aiMathSuperpower'),
              })}
            />
            <SellPointBox
              imageClassName="lg:-ml-4 lg:-mt-4"
              imageSize={{ width: 100, height: 88 }}
              imageSrcKey="onboarding/mapping-quiz/Mastery track.png"
              text={t('registerAccount:aiPersonalizedMasteryTrack')}
            />
            <SellPointBox
              imageClassName="lg:-ml-6"
              imageSize={{ width: 100, height: 66 }}
              imageSrcKey="onboarding/hom-methods/hat.svg"
              text={CarriculumText}
            />
          </div>
        </div>
      </div>
      <Footer />
    </>
  )
}
